/**
 * ColorNameInput
 * -------------
 * Input avec autocomplétion sur sellerie_colors.json.
 * Remplace le <input type="text"> de saisie de couleur dans MatiereModal.
 *
 * Usage :
 *   <ColorNameInput
 *     value={couleurInput}
 *     onChange={(nom, hex) => {
 *       setCouleurInput(nom);
 *       if (hex && !couleurHexManuelle) setNouvelleCouleurHex(hex);
 *     }}
 *     onKeyDown={(e) => e.key === 'Enter' && addCouleur()}
 *     lang="fr"
 *     placeholder="Ajouter une couleur"
 *     className="input-field flex-1"
 *   />
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import couleursStructured from './sellerie_colors.json';

// ─── Types ────────────────────────────────────────────────────────────────────

type SupportedLang = 'en' | 'fr' | 'es' | 'de' | 'it' | 'nl' | 'pt' | 'sv';

interface ColorEntry {
  name_en: string;
  name_fr: string;
  name_es: string;
  name_de: string;
  name_it: string;
  name_nl: string;
  name_pt: string;
  name_sv: string;
  hex: string;
  category: string;
}

interface ColorNameInputProps {
  value: string;
  /** Appelé à chaque changement. `hex` est null si aucune couleur reconnue sans ambiguïté. */
  onChange: (nom: string, hex: string | null) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  lang?: SupportedLang;
  placeholder?: string;
  className?: string;
}

// ─── Données ──────────────────────────────────────────────────────────────────

const colors = couleursStructured as ColorEntry[];

// ─── Normalisation ────────────────────────────────────────────────────────────

const normalize = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ');

// ─── inferHex ─────────────────────────────────────────────────────────────────
//
// Règles (par priorité) :
//
// 1. Exact match dans la langue active + EN → hex immédiat.
//
// 2. Exact match dans les autres langues (≥ 4 chars seulement, pour éviter
//    les collisions sur des mots courts comme "Bla" → suédois "Blå").
//
// 3. Prefix-in-name dans lang active + EN (≥ 3 chars) :
//    - n doit démarrer un mot dans le nom de couleur (word-boundary gauche)
//    - Ambigu = plusieurs HEX distincts matchés → retourne null
//    - Plusieurs noms → même HEX = pas ambigu, retourne ce HEX
//
// Jamais de correspondance si saisie < 2 chars.

export const inferHex = (inp: string, lang: SupportedLang = 'fr'): string | null => {
  const n = normalize(inp);
  if (!n || n.length < 2) return null;

  const langKey = `name_${lang}` as keyof ColorEntry;
  const primaryNames = (c: ColorEntry): string[] =>
    Array.from(new Set([c[langKey] as string, c.name_en])).filter(Boolean);

  // 1. Exact — lang active + EN
  for (const c of colors) {
    if (primaryNames(c).some((name) => normalize(name) === n)) return c.hex;
  }

  // 2. Exact — autres langues (min 4 chars pour éviter "Bla" → "Blå" suédois)
  if (n.length >= 4) {
    const otherLangs = (['es', 'de', 'it', 'nl', 'pt', 'sv'] as SupportedLang[]).filter(
      (l) => l !== lang
    );
    for (const c of colors) {
      const otherNames = otherLangs
        .map((l) => c[`name_${l}` as keyof ColorEntry] as string)
        .filter(Boolean);
      if (otherNames.some((name) => normalize(name) === n)) return c.hex;
    }
  }

  // 3. Prefix-in-name — min 3 chars, lang active + EN, non-ambigu par HEX
  if (n.length >= 3) {
    const matchedColors: ColorEntry[] = [];

    for (const c of colors) {
      const matched = primaryNames(c).some((name) => {
        const k = normalize(name);
        const idx = k.indexOf(n);
        return idx !== -1 && (idx === 0 || k[idx - 1] === ' ');
      });
      if (matched) matchedColors.push(c);
    }

    const uniqueHexes = Array.from(new Set(matchedColors.map((c) => c.hex)));
    // Un seul HEX possible (même si plusieurs noms pointent dessus) → pas ambigu
    if (uniqueHexes.length === 1) return uniqueHexes[0];
    // Plusieurs HEX → ambigu, on n'impose rien
  }

  return null;
};

// ─── getSuggestions ───────────────────────────────────────────────────────────

const getSuggestions = (input: string, lang: SupportedLang): ColorEntry[] => {
  const n = normalize(input);
  if (n.length < 2) return [];

  const langKey = `name_${lang}` as keyof ColorEntry;
  const targets = (c: ColorEntry): string[] =>
    Array.from(new Set([c[langKey] as string, c.name_en])).map(normalize).filter(Boolean);

  const startsWith: ColorEntry[] = [];
  const contains: ColorEntry[] = [];

  for (const c of colors) {
    const t = targets(c);
    if (t.some((k) => k.startsWith(n))) {
      startsWith.push(c);
    } else if (t.some((k) => k.includes(n))) {
      contains.push(c);
    }
    if (startsWith.length + contains.length >= 50) break;
  }

  return [...startsWith, ...contains].slice(0, 30);
};

// ─── Composant ────────────────────────────────────────────────────────────────

const ColorNameInput: React.FC<ColorNameInputProps> = ({
  value,
  onChange,
  onKeyDown,
  lang = 'fr',
  placeholder = 'Ajouter une couleur',
  className = 'input-field flex-1',
}) => {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(() => getSuggestions(value, lang), [value, lang]);

  // Fermer si clic extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setOpen(suggestions.length > 0);
    setActiveIdx(-1);
  }, [suggestions]);

  const selectColor = useCallback(
    (c: ColorEntry) => {
      const langKey = `name_${lang}` as keyof ColorEntry;
      const nom = (c[langKey] as string) || c.name_en;
      onChange(nom, c.hex);
      setOpen(false);
      setActiveIdx(-1);
      inputRef.current?.focus();
    },
    [lang, onChange]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange(next, inferHex(next, lang));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, -1));
        return;
      }
      if (e.key === 'Enter' && activeIdx >= 0) {
        // Enter sur une suggestion : sélectionne sans déclencher addCouleur()
        e.preventDefault();
        selectColor(suggestions[activeIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setActiveIdx(-1);
        return;
      }
    }
    // Toutes les autres touches (dont Enter sans suggestion active) remontent au parent
    onKeyDown?.(e);
  };

  const nameForLang = (c: ColorEntry): string =>
    (c[`name_${lang}` as keyof ColorEntry] as string) || c.name_en;

  return (
    <div ref={containerRef} className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        className={className}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-haspopup="listbox"
      />

      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto"
          style={{ maxHeight: '260px' }}
        >
          {suggestions.map((c, idx) => {
            const displayName = nameForLang(c);
            const isActive = idx === activeIdx;

            return (
              <li
                key={`${c.name_en}-${c.hex}`}
                role="option"
                aria-selected={isActive}
                className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-800'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault(); // évite le blur avant la sélection
                  selectColor(c);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
              >
                {/* Swatch */}
                <span
                  className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0"
                  style={{ backgroundColor: c.hex }}
                />

                {/* Nom dans la langue active */}
                <span className="font-medium">{displayName}</span>

                {/* Nom EN en secondaire si différent */}
                {c.name_en !== displayName && (
                  <span className="text-gray-400 text-xs truncate">{c.name_en}</span>
                )}

                {/* HEX */}
                <span className="ml-auto font-mono text-[11px] text-gray-400 flex-shrink-0">
                  {c.hex}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ColorNameInput;
